/**
 * Persistent Navigation Header - Simplified
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { History, User, ArrowLeft, Play, Watch } from 'lucide-react';
import { Button } from './ui/button';
import { useAuthStore, useSessionStore } from '../store';
import ShareWatchModal from './ShareWatchModal';
import api from '../services/api';

// Custom Paddle/Racket icon for sports
const PaddleIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <ellipse cx="12" cy="9" rx="5" ry="7" />
    <line x1="12" y1="16" x2="12" y2="22" />
    <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
    <circle cx="10" cy="10" r="0.5" fill="currentColor" stroke="none" />
    <circle cx="14" cy="10" r="0.5" fill="currentColor" stroke="none" />
    <circle cx="11" cy="6" r="0.5" fill="currentColor" stroke="none" />
    <circle cx="13" cy="6" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);
export const Navbar = ({ showBack = false, title = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const { club, court, expiresAt, clearSession, setSession } = useSessionStore();
  const [showShareWatch, setShowShareWatch] = useState(false);
  const [apiSessionActive, setApiSessionActive] = useState(false);
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Check session via API to sync with FloatingReplayButton
  useEffect(() => {
    let isMounted = true;
    
    const checkApiSession = async () => {
      if (!isAuthenticated || !user) {
        setApiSessionActive(false);
        return;
      }
      
      try {
        const response = await api.get('/session');
        if (!isMounted) return;
        
        if (response.data && response.data.court_id) {
          setApiSessionActive(true);
          // Sync local store with API data if needed
          if (!club || !court) {
            setSession({
              club: response.data.club,
              court: response.data.court_id,
              expires_at: response.data.expires_at
            });
          }
        } else {
          setApiSessionActive(false);
          // Clear local store if API says no session
          if (club || court) {
            clearSession();
          }
        }
      } catch (err) {
        if (isMounted) {
          setApiSessionActive(false);
        }
      }
    };
    
    checkApiSession();
    
    // Recheck when route changes or every 15 seconds
    const interval = setInterval(checkApiSession, 15000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isAuthenticated, user, location.pathname, club, court, clearSession, setSession]);
  
  // Use API session state as source of truth (synced with FloatingButton)
  const hasActiveSession = apiSessionActive;
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };
  
  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="sticky top-0 z-50 bg-[#070A0F]/90 backdrop-blur-xl border-b border-white/8"
    >
      <div className="flex items-center justify-between p-3 md:p-4 max-w-4xl mx-auto">
        {/* Left side - Logo (Home) or Back button + Title */}
        <div className="flex items-center gap-3">
          {showBack ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="shrink-0 h-10 w-10 md:h-11 md:w-11 text-slate-400 hover:text-[#BBFF00] hover:bg-[#BBFF00]/10"
                data-testid="nav-back-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              {title && (
                <h1 className="font-display font-bold text-lg md:text-xl text-slate-50">{title}</h1>
              )}
            </>
          ) : (
            /* Logo as Home button */
            <button
              onClick={() => navigate('/home')}
              className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
              data-testid="nav-logo-home"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#BBFF00] flex items-center justify-center shadow-[0_0_15px_rgba(187,255,0,0.3)]">
                <Play className="w-4 h-4 md:w-5 md:h-5 text-[#070A0F] fill-current" />
              </div>
              <div className="hidden sm:block">
                <span className="font-display font-bold text-base md:text-lg text-slate-50">Clipper</span>
              </div>
            </button>
          )}
        </div>
        
        {/* Right side - Navigation */}
        <nav className="flex items-center gap-1 md:gap-2">
          {/* Cancha/Court section - ALWAYS visible */}
          <Button
            variant={isActive('/cancha') || isActive('/app') ? 'secondary' : 'ghost'}
            onClick={() => navigate('/cancha')}
            className={`relative h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-5 transition-colors duration-200 ${
              hasActiveSession 
                ? 'text-[#BBFF00] bg-[#BBFF00]/10 border border-[#BBFF00]/30' 
                : 'text-slate-400 hover:text-[#BBFF00] hover:bg-[#BBFF00]/5'
            }`}
            data-testid="nav-cancha-btn"
          >
            <PaddleIcon className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">
              {hasActiveSession ? 'Mi Cancha' : 'Activa tu cancha'}
            </span>
            {/* Active session indicator */}
            {hasActiveSession && (
              <span className="absolute top-0.5 right-0.5 md:top-1 md:right-1 w-2.5 h-2.5 bg-[#BBFF00] rounded-full court-active-pulse border-2 border-[#070A0F]" />
            )}
          </Button>
          
          {/* Watch button - Share smartwatch link */}
          <Button
            variant="ghost"
            onClick={() => {
              console.log('Watch button clicked');
              setShowShareWatch(true);
            }}
            className="h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-5 text-slate-400 hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors duration-200"
            title="Abrir en Smartwatch"
            data-testid="nav-watch-btn"
          >
            <Watch className="shrink-0" />
            <span className="hidden lg:inline ml-2 text-sm font-medium">Watch</span>
          </Button>
          
          <Button
            variant={isActive('/replays') ? 'secondary' : 'ghost'}
            onClick={() => navigate('/replays')}
            className={`h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-5 transition-colors duration-200 ${
              isActive('/replays') 
                ? 'text-[#BBFF00] bg-[#131B2A]' 
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#131B2A]'
            }`}
            data-testid="nav-replays-btn"
          >
            <History className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Replays</span>
          </Button>
          
          <Button
            variant={isActive('/profile') ? 'secondary' : 'ghost'}
            onClick={() => navigate('/profile')}
            className={`h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-5 transition-colors duration-200 ${
              isActive('/profile') 
                ? 'text-[#BBFF00] bg-[#131B2A]' 
                : 'text-slate-400 hover:text-slate-200 hover:bg-[#131B2A]'
            }`}
            data-testid="nav-profile-btn"
          >
            <User className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Perfil</span>
          </Button>
        </nav>
      </div>
      
      {/* Share Watch Modal */}
      <ShareWatchModal isOpen={showShareWatch} onClose={() => setShowShareWatch(false)} />
    </motion.header>
  );
};

export default Navbar;
